import { useState } from 'react';
import { BillingDashboard } from '@/components/Billing/BillingDashboard';
import { ActiveFoldersModal } from '@/components/Billing/ActiveFoldersModal';
import { InvoicesList } from '@/components/Billing/InvoicesList';
import { Modal } from '@/components/ui/Modal';
import { FileText, CreditCard, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SepaMandateSetup } from '@/components/Billing';
import { useSepaStatus } from '@/hooks/useBilling';

export function BillingPage() {
  const { t, i18n } = useTranslation();
  const { data: sepaStatus } = useSepaStatus();
  const [showActiveFolders, setShowActiveFolders] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [selectedInvoiceUuid, setSelectedInvoiceUuid] = useState<string | null>(
    null
  );

  const handleViewActiveFolders = () => {
    setShowActiveFolders(true);
  };

  const handleViewInvoiceHistory = () => {
    setShowInvoices(true);
  };

  const handleViewInvoiceDetails = (invoiceUuid: string) => {
    setSelectedInvoiceUuid(invoiceUuid);
    setShowInvoiceDetails(true);
  };

  // Grace Period View
  if (sepaStatus?.isInGracePeriod) {
    const gracePeriodEnd = sepaStatus.gracePeriodEnd
      ? new Date(sepaStatus.gracePeriodEnd).toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : 'N/A';

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
                <CreditCard className="text-primary-600 h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-neutral-900">
                  {t('billing.title', 'Facturation')}
                </h1>
                <p className="text-lg text-neutral-600">
                  {t('billing.subtitle', "Gérez votre facturation à l'usage")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Grace Period Information Card */}
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            <div className="bg-blue-100 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full">
              <Info className="text-blue-600 h-8 w-8" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-blue-900 text-xl font-bold">
                  {t(
                    'billing.gracePeriodPage.title',
                    'Transition vers le nouveau système'
                  )}
                </h2>
                <p className="text-blue-700 mt-1 font-medium">
                  {t(
                    'billing.gracePeriodPage.subtitle',
                    'Information importante concernant votre facturation'
                  )}
                </p>
              </div>

              <div className="space-y-4 text-blue-800">
                <p className="leading-relaxed">
                  {t(
                    'billing.gracePeriodPage.description',
                    "Vous bénéficiez actuellement d'une période de grâce suite à votre précédent abonnement. Durant cette période, vous pouvez utiliser toutes les fonctionnalités d'automatisation sans restriction et sans prélèvement."
                  )}
                </p>

                <div className="bg-blue-100/50 rounded-xl p-4">
                  <p className="font-semibold">
                    {t(
                      'billing.gracePeriodPage.nextSteps',
                      "À partir du {{date}}, le nouveau système de facturation à l'usage entrera en vigueur (1€ par dossier utilisé).",
                      { date: gracePeriodEnd }
                    )}
                  </p>
                </div>

                <p className="leading-relaxed">
                  {t(
                    'billing.gracePeriodPage.action',
                    'Pour éviter toute interruption de service à cette date, enregistrez votre carte bancaire dès maintenant. Aucun paiement ne sera effectué avant la fin de votre période de grâce.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Enregistrement du moyen de paiement */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-6">
          <div className="mb-6 flex items-center gap-3">
            <CreditCard className="text-primary-600 h-6 w-6" />
            <h2 className="text-xl font-bold text-neutral-900">
              {t('billing.sepa.title', 'Carte bancaire')}
            </h2>
          </div>
          <SepaMandateSetup hideGracePeriodBanner={true} />
        </div>

        {/* Billing Dashboard - Vault usage visible même en période de grâce */}
        <BillingDashboard
          onViewActiveFolders={handleViewActiveFolders}
          onViewInvoiceHistory={handleViewInvoiceHistory}
        />

        {/* Modals */}
        <ActiveFoldersModal
          isOpen={showActiveFolders}
          onClose={() => setShowActiveFolders(false)}
        />

        <Modal
          isOpen={showInvoices}
          onClose={() => setShowInvoices(false)}
          title={t('billing.invoices.title', 'Historique des factures')}
          size="xl"
        >
          <InvoicesList onViewDetails={handleViewInvoiceDetails} />
        </Modal>

        <Modal
          isOpen={showInvoiceDetails && selectedInvoiceUuid !== null}
          onClose={() => {
            setShowInvoiceDetails(false);
            setSelectedInvoiceUuid(null);
          }}
          title={t('billing.invoice.details', 'Détails de la facture')}
          size="lg"
        >
          <div className="p-4">
            <p className="text-neutral-600">
              {t(
                'billing.invoice.detailsDescription',
                'Les détails de la facture seront affichés ici.'
              )}
            </p>
            <p className="mt-2 text-sm text-neutral-500">
              {t('billing.invoice.comingSoon', 'Fonctionnalité à implémenter')}
            </p>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
              <CreditCard className="text-primary-600 h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                {t('billing.title', 'Facturation')}
              </h1>
              <p className="text-lg text-neutral-600">
                {t('billing.subtitle', "Gérez votre facturation à l'usage")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Dashboard */}
      <BillingDashboard
        onViewActiveFolders={handleViewActiveFolders}
        onViewInvoiceHistory={handleViewInvoiceHistory}
      />

      {/* Moyen de paiement — pas pour les comptes dispensés (internes, tests) :
          rien à enregistrer, rien à afficher. */}
      {!sepaStatus?.exempt && (
        <div className="rounded-2xl border border-neutral-200 bg-white p-6">
          <div className="mb-6 flex items-center gap-3">
            <FileText className="text-primary-600 h-6 w-6" />
            <h2 className="text-xl font-bold text-neutral-900">
              {t('billing.paymentMethods.title', 'Moyens de paiement')}
            </h2>
          </div>

          <div className="rounded-xl border border-neutral-200 p-4">
            <div className="mb-2 flex items-center gap-2">
              <CreditCard className="text-primary-600 h-5 w-5" />
              <h3 className="text-heading text-base">
                {t('billing.sepa.title', 'Carte bancaire')}
              </h3>
            </div>
            {!sepaStatus?.hasValidMandate && (
              <p className="text-muted mb-4 text-sm">
                {t(
                  'billing.sepa.description',
                  'Enregistrez une carte bancaire : vos factures sont réglées automatiquement chaque mois.'
                )}
              </p>
            )}
            <SepaMandateSetup />
          </div>
        </div>
      )}

      {/* Modals */}
      <ActiveFoldersModal
        isOpen={showActiveFolders}
        onClose={() => setShowActiveFolders(false)}
      />

      <Modal
        isOpen={showInvoices}
        onClose={() => setShowInvoices(false)}
        title={t('billing.invoices.title', 'Historique des factures')}
        size="xl"
      >
        <InvoicesList onViewDetails={handleViewInvoiceDetails} />
      </Modal>

      <Modal
        isOpen={showInvoiceDetails && selectedInvoiceUuid !== null}
        onClose={() => {
          setShowInvoiceDetails(false);
          setSelectedInvoiceUuid(null);
        }}
        title={t('billing.invoice.details', 'Détails de la facture')}
        size="lg"
      >
        <div className="p-4">
          <p className="text-neutral-600">
            {t(
              'billing.invoice.detailsDescription',
              'Les détails de la facture seront affichés ici.'
            )}
          </p>
          <p className="mt-2 text-sm text-neutral-500">
            {t('billing.invoice.comingSoon', 'Fonctionnalité à implémenter')}
          </p>
        </div>
      </Modal>
    </div>
  );
}
